<template>
  <v-layout align-start class="group-filter-row">
    <v-flex :class="{ 'locked-field': filter.field.fixed }">
      <v-text-field
        v-if="checkFixed"
        label="Attribute"
        :class="{ 'background-dark': dark }"
        :value="filter.field.text"
        disabled
        append-icon="lock"
      />

      <v-autocomplete
        v-else
        label="Attribute"
        :value="filter.field.value"
        :class="{ 'background-dark': dark }"
        :items="getFieldOptions"
        :rules="[(v) => validationRules.checkRequired(v, 'Attribute')]"
        @change="changeAttr"
        return-object
        menu-props="auto, overflowY"
      />
    </v-flex>
    <v-flex class="ml-4">
      <v-select
        label="Operator"
        :value="filter.operator"
        :class="{ 'background-dark': dark }"
        :disabled="!filter.field"
        :items="preparedOptions"
        :rules="[(v) => validationRules.checkRequired(v, 'Operator')]"
        @change="changeOperator"
        item-text="label"
        return-object
      />
    </v-flex>
    <v-flex class="ml-4" v-if="showValueInput">
      <template v-if="checkFieldType">
        <v-autocomplete
          v-if="inSection && autocompleteFields.includes(filter.field.field)"
          :label="getValueLabel"
          v-model="filter.value"
          @change="loadEventFieldsCheck"
          :class="{ 'background-dark': dark }"
          :rules="[(v) => validationRules.checkRequired(v, 'Value')]"
          :multiple="filter.operator && filter.operator.list"
          :disabled="!filter.field && !filter.operator"
          :items="suggestions"
        />
        <v-combobox
          v-else
          :label="getValueLabel"
          v-model="filter.value"
          :search-input.sync="search"
          :class="{ 'background-dark': dark }"
          @update:search-input="handleSearch"
          @change="handleComboboxChange"
          :rules="[(v) => validationRules.checkRequired(v, 'Value')]"
          :items="suggestions"
          :disabled="!filter.field && !filter.operator"
          :multiple="filter.operator && filter.operator.list"
          :small-chips="filter.operator && filter.operator.list"
          hide-selected
        />
      </template>
      <form-field
        v-else
        v-model="filter.value"
        :schema="getSchema"
        :disabled="!filter.field && !filter.operator"
        field="value"
      />
    </v-flex>
    <v-flex
      class="ml-4 mt-2"
      v-if="(!filter.field || !checkFixed) && !tableFilter"
    >
      <v-btn fab small text @click="$emit('delete')"
        ><v-icon color="appMain">clear</v-icon></v-btn
      >
    </v-flex>

    <v-btn
      v-if="tableFilter"
      large
      fab
      icon
      class="refresh-button ml-4"
      @click="$emit('refresh')"
    >
      <v-icon color="appMain">refresh</v-icon>
    </v-btn>
  </v-layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { makeOperatorOptions } from './groupHelpers';
import FormField from '@/components/formFields/FormField';
import validationRules from '@/lib/validationRules';
import { capitalize } from '@/lib/misc.js';

export default {
  name: 'GroupFilterRow',
  components: {
    FormField,
  },
  props: {
    filter: {
      type: Object,
      default: () => ({}),
    },
    preparedFields: {
      type: Array,
    },
    eventFields: {},
    type: {
      type: String,
    },
    tableFilter: {
      type: Boolean,
      default: false,
    },
    inSection: {
      type: Boolean,
    },
    eventType: {
      type: String,
    },
    dark: {
      type: Boolean,
    },
    teamFilterOff: Boolean,
  },
  data() {
    return {
      search: '',
      suggestions: [],
      validationRules,
      searchableFields: ['string', 'list', 'email', 'textarea', 'user_list'],
      autocompleteFields: ['event', 'campaign', 'lesson'],
    };
  },
  watch: {
    filter() {
      this.initFilterRow();
    },
  },
  computed: {
    ...mapGetters([
      'triggersNames',
      'triggersNamesLoaded',
      'lessonsNames',
      'lessonsNamesLoaded',
    ]),
    ...mapGetters('users', ['usersOptions']),

    ...mapState('campaigns', ['campaignNames', 'campaignNamesLoaded']),

    showValueInput() {
      return (
        !this.filter.operator ||
        (this.filter.operator && this.filter.operator.operator !== '$exists')
      );
    },

    preparedOptions() {
      if (!this.filter.field) return [];
      return makeOperatorOptions(this.filter.field, this.type === 'additional');
    },

    checkFieldType() {
      const field = this.filter.field;
      return this.searchableFields.includes(field.type);
    },

    checkFixed() {
      let filterField = this.filter.field;
      if (!filterField) return false;
      if (filterField.fixed) {
        const isTrigger =
          this.eventType === 'event' && filterField.value.includes('.trigger');
        return !isTrigger;
      }
      return false;
    },

    getFieldOptions() {
      if (!this.filter.field || !this.checkFixed) {
        return this.preparedFields;
      } else {
        return [this.filter.field];
      }
    },

    getValueLabel() {
      const field = this.filter.field;
      if (field && field.field === 'progress') {
        return 'Percent, %';
      }
      return 'Value';
    },

    getSchema() {
      const field = this.filter.field;
      const operator = this.filter.operator;
      let options = [];
      let type = 'Select';
      let uppercase = false;
      let title = operator.relative ? 'Days Ago' : 'Value';

      if (
        field.type === 'date' ||
        operator.type === 'date' ||
        field.type === 'time'
      ) {
        type = 'Date';
      }
      if (field.type === 'int' || operator.relative) {
        type = 'Number';
      }
      if (operator.list) {
        type = 'Tags';
        uppercase = true;
      }
      if (field.type === 'bool') {
        options = [
          { text: 'True', value: 'true' },
          { text: 'False', value: 'false' },
        ];
      }

      return {
        group: '',
        dark: this.dark,
        title,
        type,
        options,
        uppercase,
        validators: ['required'],
      };
    },

    isEventRow() {
      return this.filter.field && this.filter.field.field === 'event';
    },
  },
  methods: {
    ...mapActions('campaigns', ['loadCampaignNames']),
    ...mapActions('users', ['loadUsers']),
    ...mapActions([
      'loadContactSuggestions',
      'loadTriggersNames',
      'loadLessonsNames',
    ]),
    handleComboboxChange(val) {
      this.loadEventFieldsCheck(val);
      if (['list', 'user_list'].includes(this.filter.field.type)) {
        this.search = '';
      }
    },
    loadEventFieldsCheck(val) {
      if (this.isEventRow) {
        this.$emit('loadEventFields', val);
      }
    },
    changeAttr(field) {
      this.filter.field = field;
      this.setDefaultOperator();
      this.handleSearch();
    },

    handleSearch(query) {
      if (this.isEventRow) return;

      let field = this.filter.field;

      if (field.type === 'user_list') {
        this.loadUsers().then(() => {
          const q = (query || '').toLowerCase();
          this.suggestions = this.usersOptions
            .filter((u) => !q || u.label.toLowerCase().includes(q))
            .map((u) => ({ text: u.label, value: u.value }));
        });
        return;
      }

      if (this.checkResourceNames(field)) return;

      this.loadContactSuggestions({
        field: field.value || field.field,
        query: query || '',
        teamFilterOff: this.teamFilterOff,
      }).then((suggestions) => {
        this.suggestions = suggestions;
      });
    },

    checkResourceNames(field) {
      let resourceKey = field.field + 's';
      let ResourceKey = capitalize(resourceKey);
      let namesLoader = `load${ResourceKey}Names`;

      if (!this[namesLoader]) return false;

      if (this[`${resourceKey}NamesLoaded`]) {
        this.setResourceNamesAsSuggestion(resourceKey);
        return true;
      }

      this[`load${ResourceKey}Names`]().then(() => {
        this.setResourceNamesAsSuggestion(resourceKey);
      });

      return true;
    },

    setResourceNamesAsSuggestion(resourceKey) {
      this.suggestions = this[`${resourceKey}Names`];
      if (this.filter.value && typeof this.filter.value === 'string') {
        this.filter.value = this.suggestions.find(
          (i) => i.value === this.filter.value
        );
      }
    },

    changeOperator(operator) {
      this.filter.operator = operator;

      if (operator.operator === '$exists') {
        this.filter.value = operator.value === '$exists';
      } else {
        this.filter.value = '';
      }
    },
    setDefaultOperator() {
      let operator = this.preparedOptions.find((i) => i.operator === '$eq');
      if (operator) {
        this.changeOperator(operator);
      }
    },
    initFilterRow() {
      if (this.filter.operator && typeof this.filter.operator === 'string') {
        let fullOperator = this.preparedOptions.find(
          (i) => i.value === this.filter.operator
        );

        if (fullOperator) {
          this.filter.operator = fullOperator;
        } else {
          let isRelative = !isNaN(Date.parse(this.filter.value));
          this.filter.operator = this.preparedOptions.find((i) => {
            return (
              i.operator === this.filter.operator && !!i.relative == isRelative
            );
          });
        }

        if (this.filter?.operator?.list && !Array.isArray(this.filter.value)) {
          this.filter.value = this.filter.value.split(',');
        }
      }

      this.checkSuggestionsForEvents();
    },
    checkSuggestionsForEvents() {
      if (!this.filter.field || this.filter.field.field !== 'event') return;
      this.suggestions =
        this.eventType === 'event'
          ? this.preparedFields.filter((i) => i.is_event).map((i) => i.field)
          : [this.filter.value];
    },
  },
  mounted() {
    this.initFilterRow();
    this.checkResourceNames(this.filter.field);
  },
};
</script>

<style lang="less">
.locked-field {
  max-width: 130px;

  .v-input--is-disabled {
    opacity: 1;

    .v-input__slot:before {
      display: none;
    }
  }
}

.group-filter-row {
  .v-btn.refresh-button {
    height: 52px !important;
    width: 52px !important;
  }

  .field__inner-title {
    display: none;
  }
}
</style>
